import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Observer } from "@babylonjs/core/Misc/observable";
import { Scene } from "@babylonjs/core/scene";

// Effet de bord requis : enregistre le composant de scène qui déclenche le
// rendu des shadow maps. Sans lui, le ShadowGenerator reste inerte.
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";

import type { GameScene, SceneContext } from "@/core/scene/types";
import type { ForestBuilderOptions } from "@/entities/ForestBuilderCharacter";
import { ForestBuilderCharacter } from "@/entities/ForestBuilderCharacter";
import type { GameAction } from "@/input/actions";
import { ChunkedGround } from "@/world/ChunkedGround";
import { Terrain } from "@/world/terrain";
import type { Tree, TreeFieldOptions } from "@/world/TreeField";
import { TreeField } from "@/world/TreeField";

/**
 * Réglages de la scène.
 *
 * Ils n'existent que pour les tests : ceux-ci fournissent les octets du GLB lus
 * sur disque au lieu de l'URL servie par le jeu, et éclaircissent le semis quand
 * la marche à mesurer doit rester libre.
 */
export interface MainSceneOptions {
  readonly character: Partial<ForestBuilderOptions>;
  readonly trees: Partial<TreeFieldOptions>;
}

/** Radians par seconde, soit un demi-tour complet en une seconde. */
const PLAYER_TURN_SPEED = Math.PI;

/**
 * Durée d'avance ininterrompue au bout de laquelle le personnage prend son élan,
 * en secondes.
 *
 * Assez long pour qu'aucun trajet ordinaire — contourner un tronc, se replacer
 * face à un arbre — ne le déclenche, assez court pour que la première ligne
 * droite un peu tenue le donne.
 */
const SPRINT_DELAY = 3;

/** Multiplicateur appliqué à la vitesse une fois l'élan pris. */
const SPRINT_FACTOR = 3;

/**
 * Portée de la coupe, mesurée de l'axe du personnage à l'écorce.
 *
 * La capsule fait 0,30 d'épaisseur : collé au tronc, le joueur est déjà à 0,30.
 * 1,2 laisse donc un peu plus d'un demi-mètre de battement — assez pour ne pas
 * obliger à se coller parfaitement, trop peu pour couper un arbre qu'on ne
 * touche visiblement pas.
 */
const CHOP_REACH = 1.2;

/** Demi-angle du cône de visée : l'arbre doit être devant, pas seulement près. */
const CHOP_HALF_ANGLE = Math.PI / 4;

/** Couleur du ciel. Le brouillard porte la même : voir `createAtmosphere`. */
const SKY_COLOR = new Color3(0.043, 0.058, 0.078);

/**
 * Densité du brouillard exponentiel.
 *
 * Réglage à deux contraintes opposées. Trop dense, il ne masque pas le bord du
 * monde : il supprime l'horizon, et le relief cesse de se lire faute de recul.
 * Trop léger, la grille de chunks se termine à l'écran par une arête franche.
 *
 * La visibilité vaut `exp(-(d × densité)²)`, mesurée **depuis la caméra**. Le
 * bord de la grille est garanti à 240 unités du personnage ; la caméra pouvant
 * reculer de 32 unités et le joueur orbiter pour se regarder de face, le bord le
 * plus proche qu'un œil puisse atteindre est à 208 unités. À cette valeur il en
 * reste 2,8 % — indiscernable du ciel — tandis qu'à 100 unités la moitié du
 * paysage passe encore, de quoi voir deux ou trois collines devant soi.
 */
const FOG_DENSITY = 0.009;

/**
 * Direction de la lumière du soleil — **rasante**, et c'est tout l'enjeu.
 *
 * Un soleil au zénith éclaire presque identiquement toutes les faces d'un
 * terrain doux : leurs normales sont toutes proches de la verticale, l'ombrage
 * ne les sépare pas, et des collines bien réelles s'affichent comme un plan. Bas
 * sur l'horizon, le même relief prend deux fois plus de contraste entre versants
 * exposés et versants à l'ombre. C'est le principal révélateur du modelé.
 */
const SUN_DIRECTION = new Vector3(-0.55, -0.45, -0.7);

/** Position du soleil relativement au joueur, qu'il suit à la trace. */
const SUN_OFFSET = SUN_DIRECTION.scale(-28);

/**
 * Scène principale : un bûcheron dans une forêt sans fin.
 *
 * Elle assemble des pièces qui s'ignorent — le champ de hauteur, le sol pavé, le
 * semis d'arbres, le personnage — et n'ajoute qu'une chose : la conduite. Le
 * personnage ne connaît pas le terrain, le terrain ne connaît personne ; c'est
 * ici que le pas commandé par le joueur est reposé sur le sol à chaque image.
 *
 * Les règles qui n'appartiennent à aucune de ces pièces tiennent au même
 * endroit : l'élan pris au bout de quelques secondes d'avance, et la récolte
 * publiée dans `context.harvest`, que le HUD affiche sans rien savoir d'ici.
 */
class MainScene implements GameScene {
  readonly scene: Scene;

  private readonly context: SceneContext;
  private readonly terrain: Terrain;
  private readonly ground: ChunkedGround;
  private readonly trees: TreeField;
  private readonly character: ForestBuilderCharacter;
  private readonly camera: ArcRotateCamera;
  private readonly sun: DirectionalLight;

  /**
   * Cap du personnage, en radians.
   *
   * La scène le tient elle-même plutôt que de le déduire du déplacement : au
   * manche unique, l'axe horizontal fait pivoter et l'axe vertical fait avancer.
   * En marche arrière, le personnage doit donc reculer sans se retourner — ce
   * qu'un cap déduit de la direction du pas ne saurait pas faire.
   */
  private yaw = 0;

  /** Direction commandée, réutilisée d'une image à l'autre. */
  private readonly moveDirection = Vector3.Zero();

  /**
   * Temps d'avance ininterrompue, en secondes. Au-delà de `SPRINT_DELAY`, le
   * personnage court trois fois plus vite ; tout ce qui n'est pas une avance le
   * remet à zéro.
   */
  private forwardTime = 0;

  /**
   * Coupe demandée depuis la dernière image, et l'abonnement qui la remplit.
   *
   * La commande est captée par **évènement** et non en interrogeant l'état à
   * chaque image : un appui plus court que l'intervalle entre deux images —
   * chose courante dès que le taux d'images baisse — ne laisserait aucune trace
   * dans l'état, et le coup de hache serait purement et simplement perdu.
   */
  private chopRequested = false;
  private readonly chopObserver: Observer<GameAction> | null;

  /**
   * Arbre visé par la coupe en cours. Il ne tombe qu'une fois le geste terminé,
   * pas au moment où la commande est reçue.
   */
  private felling: Tree | null = null;

  private constructor(
    context: SceneContext,
    scene: Scene,
    character: ForestBuilderCharacter,
    treeOptions: Partial<TreeFieldOptions>,
  ) {
    this.context = context;
    this.scene = scene;
    this.character = character;

    this.createAtmosphere();
    this.camera = this.createCamera();
    const { sun, shadows } = this.createLighting();
    this.sun = sun;

    this.terrain = new Terrain();
    this.ground = new ChunkedGround(this.scene, this.terrain);
    this.trees = new TreeField(this.scene, this.terrain, treeOptions);

    this.chopObserver = context.input.onActionPressed.add((action) => {
      if (action === "chop") this.chopRequested = true;
    });

    // Le personnage projette son ombre ; les arbres se contentent de la recevoir.
    // Une shadow map de 1024 couvre ici une trentaine d'unités : y ajouter des
    // centaines de troncs répartirait sa définition sur toute la forêt, et c'est
    // précisément le contact des pieds avec le sol qu'elle sert à montrer.
    shadows.addShadowCaster(this.character.visualRoot, true);

    // Pose le personnage et remplit sol et semis avant le premier rendu : sinon
    // la première image le montre suspendu au-dessus du vide.
    this.settle();

    this.camera.setTarget(this.character.controller.position);
  }

  /**
   * Construit la scène, personnage compris.
   *
   * Le modèle est chargé **avant** que la scène ne soit rendue : le
   * `SceneManager` attend cette promesse et ne détruit la scène précédente
   * qu'une fois celle-ci prête, donc le joueur ne voit jamais un décor sans
   * personnage. En cas d'échec — fichier absent, animation manquante — la scène
   * à moitié construite est détruite et l'erreur remonte à `main.ts`, qui
   * l'affiche.
   */
  static async create(
    context: SceneContext,
    options: Partial<MainSceneOptions>,
  ): Promise<MainScene> {
    const scene = new Scene(context.engine);

    try {
      const character = await ForestBuilderCharacter.load(scene, options.character ?? {});
      return new MainScene(context, scene, character, options.trees ?? {});
    } catch (error) {
      scene.dispose();
      throw error;
    }
  }

  /**
   * Conduite au manche unique : l'axe vertical avance et recule, l'axe
   * horizontal fait pivoter. Les deux se cumulent — manche en haut à droite, le
   * personnage avance en virant à droite — et une entrée nulle le laisse au
   * repos.
   */
  update(deltaSeconds: number): void {
    const { input } = this.context;

    // Pendant la coupe, le personnage est planté : le manche ne le fait ni
    // avancer ni pivoter. Le lacet est donc figé lui aussi, sinon il tournerait
    // en silence et le personnage pivoterait d'un coup à la fin du geste.
    if (!this.character.busy) {
      // Le lacet ne dépend pas du sens de marche : le manche à droite fait
      // toujours tourner le nez vers la droite. C'est aussi ce qui permet de
      // pivoter sur place, à l'arrêt — indispensable quand un seul manche sert à
      // choisir le cap. Une voiture inverserait le lacet en marche arrière ; ici
      // le geste garde le même effet, quel qu'il soit.
      this.yaw += input.axis("turnLeft", "turnRight") * PLAYER_TURN_SPEED * deltaSeconds;
    }

    this.handleChop();

    // Marche le long du nez du personnage, et non de l'axe de la caméra : dès
    // lors que le joueur choisit lui-même son cap, « avancer » ne peut vouloir
    // dire que « vers là où il regarde ». L'intensité du manche est conservée
    // dans la longueur du vecteur : c'est elle qui dose la vitesse.
    const throttleAxis = input.axis("moveBackward", "moveForward");
    this.moveDirection.set(Math.sin(this.yaw) * throttleAxis, 0, Math.cos(this.yaw) * throttleAxis);

    const speedScale = this.trackSprint(throttleAxis, deltaSeconds);

    this.character.update(
      {
        direction: this.moveDirection,
        running: input.isActive("run"),
        facing: this.yaw,
        speedScale,
      },
      deltaSeconds,
    );

    this.settle();
  }

  dispose(): void {
    // L'`InputManager` survit à la scène : son observable garderait sinon une
    // référence sur une scène détruite, et le prochain appui la réveillerait.
    this.context.input.onActionPressed.remove(this.chopObserver);

    // Détruire la scène libère caméras, lumières, maillages et matériaux qu'elle
    // possède ; le personnage garde en plus des groupes d'animation, et le sol
    // comme le semis des structures JS — pools, index — qu'elle ne connaît pas.
    this.character.dispose();
    this.trees.dispose();
    this.ground.dispose();
    this.scene.dispose();
  }

  /**
   * Entretient le chronomètre d'élan et rend le multiplicateur de vitesse à
   * appliquer pour cette image.
   *
   * Seule l'**avance** compte : reculer, s'arrêter, pivoter sur place ou donner
   * un coup de hache remet le compteur à zéro. Un chronomètre que le moindre
   * arrêt ne romprait pas ferait repartir le personnage à pleine vitesse après
   * une pause, sans que le joueur ait rien demandé.
   *
   * L'élan ne tient pas compte de l'inclinaison du manche : un pouce à moitié
   * poussé pendant trois secondes y donne droit, et la vitesse reste dosée par
   * ailleurs — le multiplicateur s'applique à ce que le joueur commande, il ne
   * le remplace pas.
   */
  private trackSprint(throttleAxis: number, deltaSeconds: number): number {
    if (throttleAxis <= 0 || this.character.busy) {
      this.forwardTime = 0;
      return 1;
    }

    this.forwardTime += deltaSeconds;

    return this.forwardTime > SPRINT_DELAY ? SPRINT_FACTOR : 1;
  }

  /**
   * Consomme une demande de coupe, et fait tomber l'arbre à la fin du geste.
   *
   * La demande est consommée qu'elle aboutisse ou non : sans cela, un appui sans
   * arbre devant soi resterait en attente et déclencherait une coupe à retardement
   * au premier arbre croisé.
   */
  private handleChop(): void {
    if (this.felling && !this.character.busy) {
      this.trees.fell(this.felling);
      this.felling = null;
      // La jauge ne compte que des arbres réellement tombés : elle avance au
      // moment où le tronc disparaît, pas à la commande qui l'a visé.
      this.context.harvest.add();
    }

    const requested = this.chopRequested;
    this.chopRequested = false;

    if (!requested || this.character.busy) return;

    const { x, z } = this.character.controller.position;
    const target = this.trees.findTarget(x, z, this.yaw, CHOP_REACH, CHOP_HALF_ANGLE);
    // Sans arbre devant lui, le personnage ne moulinerait que du vide : la
    // commande est ignorée, ce qui garde le geste lisible comme une récolte.
    if (!target || !this.character.chop()) return;

    this.felling = target;
  }

  /**
   * Repose le personnage sur le sol et fait suivre ce qui dépend de sa position.
   * Appelée à chaque image, y compris à l'arrêt : il doit rester juste même
   * quand il ne fait que pivoter sur place.
   */
  private settle(): void {
    const { x, z } = this.character.controller.position;

    // Le personnage reste **droit** sur la pente, là où le cube s'y couchait :
    // un bipède garde son axe vertical quel que soit le terrain, et l'incliner
    // le ferait osciller à chaque changement de versant.
    this.character.groundHeight = this.terrain.heightAt(x, z);

    // Le soleil suit le joueur. Laissé en place, il finirait par laisser le
    // personnage hors du volume couvert par sa shadow map, et l'ombre portée —
    // le seul indice de contact avec le sol — disparaîtrait.
    this.sun.position.set(
      x + SUN_OFFSET.x,
      this.character.controller.position.y + SUN_OFFSET.y,
      z + SUN_OFFSET.z,
    );

    this.ground.update(x, z);
    this.trees.update(x, z);
  }

  /**
   * Ciel et brouillard.
   *
   * Le brouillard n'est pas décoratif : c'est lui qui rend le monde crédible en
   * dissolvant le bord de la grille de chunks avant qu'on ne le distingue. Sa
   * couleur **doit** être celle du fond, sinon le terrain lointain se détache du
   * ciel au lieu de s'y fondre — et la limite qu'on cherchait à masquer se
   * retrouve soulignée.
   */
  private createAtmosphere(): void {
    this.scene.clearColor = SKY_COLOR.toColor4(1);
    this.scene.fogMode = Scene.FOGMODE_EXP2;
    this.scene.fogColor = SKY_COLOR.clone();
    this.scene.fogDensity = FOG_DENSITY;

    // Le nouvel atlas PBR porte des écarts de matière plus fins que les aplats
    // de la V1. Une correction légère les rend lisibles sans brûler le ciel ni
    // transformer la forêt nocturne en scène de studio.
    this.scene.imageProcessingConfiguration.exposure = 1.06;
    this.scene.imageProcessingConfiguration.contrast = 1.08;
  }

  private createCamera(): ArcRotateCamera {
    // Vue basse plutôt que plongeante : c'est de profil qu'un relief doux se
    // lit, et c'est la seule inclinaison qui laisse voir l'horizon — donc le
    // paysage, plutôt que le seul carré de sol autour du personnage.
    const camera = new ArcRotateCamera(
      "camera",
      -Math.PI / 2,
      Math.PI / 2.6,
      12,
      Vector3.Zero(),
      this.scene,
    );

    // Le personnage fait deux unités de haut : sous six unités de recul, la
    // caméra lui rentre dedans.
    camera.lowerRadiusLimit = 6;
    // Reculer davantage rapprocherait l'œil du bord de la grille de chunks
    // lorsque le joueur orbite pour se regarder de face : ce plafond et
    // `FOG_DENSITY` se lisent ensemble, changer l'un demande de revoir l'autre.
    camera.upperRadiusLimit = 32;
    // Bloque bien au-dessus de l'horizon. Sur un terrain vallonné, une vue
    // rasante ne se contente pas de passer sous le sol : elle traverse la
    // colline située derrière le personnage. Un cadrage un peu plus plongeant
    // coûte moins qu'une caméra qui entre dans le décor.
    camera.upperBetaLimit = Math.PI / 2.6;
    camera.wheelDeltaPercentage = 0.01;
    camera.attachControl(this.context.canvas, true);

    return camera;
  }

  private createLighting(): { sun: DirectionalLight; shadows: ShadowGenerator } {
    // Lumière d'ambiance : évite des faces non éclairées totalement noires.
    // Volontairement discrète — elle arrive de partout à la fois, donc chaque
    // point qu'elle éclaire est un point où le modelé du sol cesse de se lire.
    const ambient = new HemisphericLight("ambient", new Vector3(0, 1, 0), this.scene);
    ambient.intensity = 0.38;
    ambient.diffuse = new Color3(0.82, 0.88, 0.96);
    ambient.groundColor = new Color3(0.1, 0.12, 0.16);

    const sun = new DirectionalLight("sun", SUN_DIRECTION.clone(), this.scene);
    sun.position = SUN_OFFSET.clone();
    sun.intensity = 1.1;
    // Le soleil se tient à ‖SUN_OFFSET‖ ≈ 28 unités du personnage, seul
    // projeteur de la scène. Une tranche de profondeur serrée, plutôt que le
    // [minZ, maxZ] de la caméra, rend toute la précision de la shadow map à la
    // seule zone qui porte une ombre. Fixe et non recalculée à chaque image :
    // une tranche qui respire fait scintiller le contour de l'ombre.
    sun.shadowMinZ = 1;
    sun.shadowMaxZ = 60;

    // Remplissage froid et discret depuis le côté opposé au soleil. Il détache
    // la silhouette du personnage et fait lire cuir, tissu et acier quand la
    // caméra orbite du côté ombré, sans produire une seconde ombre.
    const fill = new DirectionalLight("character-fill", new Vector3(0.5, -0.28, 0.62), this.scene);
    fill.intensity = 0.24;
    fill.diffuse = new Color3(0.58, 0.69, 0.84);
    fill.specular = new Color3(0.72, 0.79, 0.9);

    const shadows = new ShadowGenerator(1024, sun);
    shadows.usePercentageCloserFiltering = true;
    // `bias` reste à sa valeur par défaut (5e-5) : l'augmenter décolle l'ombre
    // de son objet jusqu'à la faire disparaître. Contre l'acné de surface, on
    // décale le long de la normale, ce qui ne détache pas l'ombre du contact.
    shadows.normalBias = 0.02;

    return { sun, shadows };
  }
}

/** Fabrique consommée par le registre de scènes. */
export function createMainScene(
  context: SceneContext,
  options: Partial<MainSceneOptions> = {},
): Promise<GameScene> {
  return MainScene.create(context, options);
}
